import { toaster } from '@/components/ui/toaster';
import { messagingSectionCountersQueryKey } from '@/hooks/queries/use-messaging-section-counters-query';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ApiResponse } from '@fluenceur/types';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import api from '@/libs/axiosInstance';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';

type ConversationActionInput = {
  conversationId: string;
};

const archiveConversation = async ({ conversationId }: ConversationActionInput): Promise<void> => {
  await api.post(`/dashboard/conversations/${conversationId}/archive`);
};

const unarchiveConversation = async ({
  conversationId,
}: ConversationActionInput): Promise<void> => {
  await api.post(`/dashboard/conversations/${conversationId}/unarchive`);
};

const deleteConversation = async ({ conversationId }: ConversationActionInput): Promise<void> => {
  await api.delete(`/dashboard/conversations/${conversationId}`);
};

const invalidateConversationQueries = (
  queryClient: QueryClient,
  brandId: string,
  conversationId: string
) => {
  queryClient.invalidateQueries({ queryKey: conversationQueryKeys.lists(brandId) });
  queryClient.invalidateQueries({ queryKey: messagingSectionCountersQueryKey(brandId) });
  queryClient.invalidateQueries({
    queryKey: conversationQueryKeys.detail(brandId, conversationId),
  });
};

export const useArchiveConversation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<void, AxiosError<ApiResponse>, ConversationActionInput>({
    mutationFn: archiveConversation,
    onSuccess: (_, variables) => {
      invalidateConversationQueries(queryClient, brandId, variables.conversationId);
      toaster.success({ title: t('messages.conversationActions.archive.success') });
    },
    onError: (error) => {
      toaster.error({
        title: t('messages.conversationActions.archive.error'),
        description: error.response?.data?.message || t('common.error'),
      });
    },
  });
};

export const useUnarchiveConversation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<void, AxiosError<ApiResponse>, ConversationActionInput>({
    mutationFn: unarchiveConversation,
    onSuccess: (_, variables) => {
      invalidateConversationQueries(queryClient, brandId, variables.conversationId);
      toaster.success({ title: t('messages.conversationActions.unarchive.success') });
    },
    onError: (error) => {
      toaster.error({
        title: t('messages.conversationActions.unarchive.error'),
        description: error.response?.data?.message || t('common.error'),
      });
    },
  });
};

export const useDeleteConversation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<void, AxiosError<ApiResponse>, ConversationActionInput>({
    mutationFn: deleteConversation,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys.lists(brandId) });
      queryClient.invalidateQueries({ queryKey: messagingSectionCountersQueryKey(brandId) });
      queryClient.removeQueries({
        queryKey: conversationQueryKeys.detail(brandId, variables.conversationId),
      });
      queryClient.removeQueries({
        queryKey: conversationQueryKeys.messagesPrefix(brandId, variables.conversationId),
      });
      toaster.success({ title: t('messages.conversationActions.delete.success') });
    },
    onError: (error) => {
      toaster.error({
        title: t('messages.conversationActions.delete.error'),
        description: error.response?.data?.message || t('common.error'),
      });
    },
  });
};
